import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { BudgetType } from '@/components/pages/job-posting/types';
import { calculateTotalBudget } from '@/components/pages/job-posting/utils/channel-utils';

export function useBudgetType() {
  const intl = useIntl();

  const budgetTypeOptions = [
    {
      value: 'lifetime',
      label: intl.formatMessage({
        defaultMessage: 'Total Budget',
        description:
          'Picker option: one lump sum for the whole campaign, not per time unit',
      }),
      id: 'lifetime',
    },
    {
      value: 'daily',
      label: intl.formatMessage({
        defaultMessage: 'Per day',
        description: 'Picker option: budget is configured as a daily cap',
      }),
      id: 'daily',
    },
    {
      value: 'weekly',
      label: intl.formatMessage({
        defaultMessage: 'Per week',
        description: 'Picker option: budget is configured as a weekly cap',
      }),
      id: 'weekly',
    },
    {
      value: 'monthly',
      label: intl.formatMessage({
        defaultMessage: 'Per month',
        description: 'Picker option: budget is configured as a monthly cap',
      }),
      id: 'monthly',
    },
  ];

  const getJobPostingBudgetLabel = useCallback(
    (budgetType?: BudgetType | null) => {
      switch (budgetType) {
        case 'daily':
          return intl.formatMessage({
            defaultMessage: 'Total spend per day',
            description:
              'Label in job posting summary: rolled-up daily budget across the window',
          });
        case 'weekly':
          return intl.formatMessage({
            defaultMessage: 'Total spend per week',
            description:
              'Label in job posting summary: rolled-up weekly budget across the window',
          });
        case 'monthly':
          return intl.formatMessage({
            defaultMessage: 'Total spend per month',
            description:
              'Label in job posting summary: rolled-up monthly budget across the window',
          });
        case 'lifetime':
        default:
          return intl.formatMessage({
            defaultMessage: 'Total spend',
            description:
              'Label for one-shot lifetime budget in posting summary',
          });
      }
    },
    [intl],
  );

  const getEditCampaignBudgetLabel = useCallback(
    (budgetType?: BudgetType | null) => {
      switch (budgetType) {
        case 'daily':
          return intl.formatMessage({
            defaultMessage: 'Budget per day',
            description:
              'Form label on edit campaign: user’s daily cap before projection',
          });
        case 'weekly':
          return intl.formatMessage({
            defaultMessage: 'Budget per week',
            description:
              'Form label on edit campaign: user’s weekly cap before projection',
          });
        case 'monthly':
          return intl.formatMessage({
            defaultMessage: 'Budget per month',
            description:
              'Form label on edit campaign: user’s monthly cap before projection',
          });
        case 'lifetime':
        default:
          return intl.formatMessage({
            defaultMessage: 'Budget',
            description:
              'Form label: total campaign spend limit (lifetime type)',
          });
      }
    },
    [intl],
  );

  // Label for budget based on budget type in calculation section shown in edit campaign and job posting flow
  const getCalculationBudgetLabel = useCallback(
    (budgetType?: BudgetType | null) => {
      switch (budgetType) {
        case 'weekly':
          return intl.formatMessage({
            defaultMessage: 'Spend per week',
            description:
              'Line item in spend breakdown: amount attributed to each week',
          });
        case 'monthly':
          return intl.formatMessage({
            defaultMessage: 'Spend per month',
            description:
              'Line item in spend breakdown: amount attributed to each month',
          });
        case 'daily':
          return intl.formatMessage({
            defaultMessage: 'Spend per day',
            description:
              'Line item in spend breakdown: amount attributed to each day',
          });
        case 'lifetime':
        default:
          return intl.formatMessage({
            defaultMessage: 'Campaign budget',
            description:
              'Line item: overall campaign limit used in the calculation',
          });
      }
    },
    [intl],
  );

  return {
    budgetTypeOptions,
    calculateTotalBudget,
    getJobPostingBudgetLabel,
    getEditCampaignBudgetLabel,
    getCalculationBudgetLabel,
  };
}
